import React, { useEffect, useState } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  NumberInput,
  FileInput,
  FileField,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
} from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import { db, storage } from "../../firebase/firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { calculateRemainingFinance, cleanDeferredInstallments, MONTH_CHOICES } from "./vehicleFinance";

const buildYearChoices = () => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current - 3; y <= current + 8; y += 1) {
    years.push({ id: y, name: String(y) });
  }
  return years;
};

export const VehicleEdit = () => {
  const [remainingDisplay, setRemainingDisplay] = useState(0);
  const yearChoices = buildYearChoices();

  // 🔹 File upload helper
  const uploadFile = async (maybeFile, folder, vehicleNumber) => {
    if (!maybeFile || !maybeFile.rawFile) return null;
    const storageRef = ref(
      storage,
      `${folder}/${vehicleNumber || "vehicle"}_${Date.now()}_${maybeFile.rawFile.name}`
    );
    await uploadBytes(storageRef, maybeFile.rawFile);
    return await getDownloadURL(storageRef);
  };

  // ✅ Date helpers for React-Admin DateInput (supports Firestore Timestamp + Date + string)
  const toDateObj = (v) => {
    if (!v) return null;

    // Firestore Timestamp (has toDate)
    if (v?.toDate && typeof v.toDate === "function") {
      const d = v.toDate();
      return isNaN(d.getTime()) ? null : d;
    }

    // Timestamp-like object
    if (typeof v === "object" && typeof v.seconds === "number") {
      const d = new Date(v.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }

    // Already Date
    if (v instanceof Date) {
      return isNaN(v.getTime()) ? null : v;
    }

    // String/number -> Date
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  // RA DateInput expects YYYY-MM-DD string for displaying
  const formatDateInput = (v) => {
    const d = toDateObj(v);
    return d ? d.toISOString().slice(0, 10) : "";
  };

  // RA DateInput gives YYYY-MM-DD string on change -> convert to Date for Firestore
  const parseDateInput = (v) => (v ? new Date(v) : null);

  // 🔹 Excel/Firestore Date Parser (safe for save)
  const parseExcelDate = (value) => {
    if (!value) return null;

    // Firestore Timestamp (has toDate)
    if (value?.toDate && typeof value.toDate === "function") {
      const d = value.toDate();
      return isNaN(d.getTime()) ? null : d;
    }

    // Firestore Timestamp (seconds)
    if (typeof value === "object" && typeof value.seconds === "number") {
      const d = new Date(value.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }

    // Already Date object
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    // React-Admin DateInput returns YYYY-MM-DD
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    // Excel numeric serial number
    if (typeof value === "number") {
      const excelEpoch = new Date(1899, 11, 30);
      const d = new Date(excelEpoch.getTime() + value * 86400 * 1000);
      return isNaN(d.getTime()) ? null : d;
    }

    // DD/MM/YYYY or DD-MM-YYYY
    if (typeof value === "string") {
      const parts = value.split(/[-/]/);
      if (parts.length === 3) {
        let d = Number(parts[0]);
        let m = Number(parts[1]) - 1;
        let y = Number(parts[2]);
        if (y < 100) y = 2000 + y;
        const dt = new Date(y, m, d);
        return isNaN(dt.getTime()) ? null : dt;
      }
    }

    return null;
  };

  // 🔹 Watcher for live updates (EDIT FIX + works with Firestore Timestamp display)
  const FormWatcher = () => {
    const { setValue } = useFormContext();

    const watched = useWatch({
      name: [
        "totalFacilityAmount",
        "monthlyInstallment",
        "installmentStart",
        "installmentEnd",
        "mortgageFrom",
        "soldAt",
        "deferredInstallments",
        "manualRevisedEndDate",
        "bankCharges",
        "settlementDate",
      ],
    });

    useEffect(() => {
      if (!watched) return;

      const total = watched[0];
      const monthly = watched[1];
      const mortgageFrom = watched[4];

      // ✅ Avoid running too early (before record loads)
      if (total == null && monthly == null && !mortgageFrom) return;

      const rem = calculateRemainingFinance(
        {
          totalFacilityAmount: total,
          monthlyInstallment: monthly,
          installmentStart: watched[2], // ✅ raw Timestamp/Date/string
          installmentEnd: watched[3],   // ✅ raw Timestamp/Date/string
          mortgageFrom,
          soldAt: watched[5],           // ✅ raw Timestamp/Date/string
          deferredInstallments: cleanDeferredInstallments(watched[6], monthly),
          manualRevisedEndDate: watched[7],
          bankCharges: watched[8],
          settlementDate: watched[9],
        },
        new Date()
      );

      setValue("remainingFinance", rem, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setRemainingDisplay(rem);
    }, [watched, setValue]);

    return null;
  };

  // 🔹 Save updated vehicle
  const handleSave = async (values) => {
    try {
      const vehicleRef = doc(db, "vehicles", values.id);

      const insuranceUrl = await uploadFile(
        values.insuranceFile,
        "vehicles/insurance",
        values.vehicleNumber
      );
      const registrationUrl = await uploadFile(
        values.registrationFile,
        "vehicles/registration",
        values.vehicleNumber
      );
      const financeUrl = await uploadFile(
        values.financeFile,
        "vehicles/finance",
        values.vehicleNumber
      );

      // 🔹 Parse before calculating (safe for Firestore)
      const parsedStart = parseExcelDate(values.installmentStart);
      const parsedEnd = parseExcelDate(values.installmentEnd);
      const parsedSold = parseExcelDate(values.soldAt);
      const parsedManualRevisedEnd = parseExcelDate(values.manualRevisedEndDate);
      const parsedSettlementDate = parseExcelDate(values.settlementDate);
      const deferredInstallments = cleanDeferredInstallments(
        values.deferredInstallments,
        values.monthlyInstallment
      );
      const parsedInsurance = parseExcelDate(values.insuranceExpiry);
      const parsedRegistration = parseExcelDate(values.registrationExpiry);

      const remainingFinance = calculateRemainingFinance(
        {
          ...values,
          installmentStart: parsedStart,
          installmentEnd: parsedEnd,
          soldAt: parsedSold,
          deferredInstallments,
          manualRevisedEndDate: parsedManualRevisedEnd,
          bankCharges: Number(values.bankCharges) || 0,
          settlementDate: parsedSettlementDate,
        },
        new Date()
      );

      const updates = {
        // identity
        company: values.company || "",
        vehicleNumber: values.vehicleNumber || "",
        vehicleType: values.vehicleType || "",
        vehicleYear: values.vehicleYear || "",

        // expiry
        insuranceExpiry: parsedInsurance,
        registrationExpiry: parsedRegistration,

        // finance
        mortgageFrom: values.mortgageFrom || "",
        installmentBank: values.installmentBank || "",
        totalFacilityAmount: values.totalFacilityAmount || null,
        monthlyInstallment: values.monthlyInstallment || null,
        installmentStart: parsedStart,
        installmentEnd: parsedEnd,
        deferredInstallments,
        manualRevisedEndDate: parsedManualRevisedEnd,
        bankCharges: Number(values.bankCharges) || 0,

        settlementDate: parsedSettlementDate,
        settlementAmount: Number(values.settlementAmount) || 0,
        settlementNotes: values.settlementNotes || "",

        remainingFinance,

        // sale
        soldAt: parsedSold,
        soldNotes: values.soldNotes || "",

        updatedAt: new Date(),
        updatedBy: localStorage.getItem("email") || "",
      };

      if (insuranceUrl) updates.insuranceFile = insuranceUrl;
      if (registrationUrl) updates.registrationFile = registrationUrl;
      if (financeUrl) updates.financeFile = financeUrl;

      await updateDoc(vehicleRef, updates);
      alert("✅ Vehicle updated successfully!");
      window.location.hash = "#/vehicles";
    } catch (err) {
      console.error("❌ Error updating vehicle:", err);
      alert("❌ " + err.message);
    }
  };

  return (
    <Edit title="Edit Vehicle">
      <SimpleForm onSubmit={handleSave}>
        <FormWatcher />

        {/* Company */}
        <div style={{ marginBottom: "10px" }}>
          <TextInput source="company" label="Company" fullWidth />
        </div>

        {/* Vehicle Info */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
          <TextInput source="vehicleNumber" label="Vehicle Number" fullWidth />
          <TextInput
            source="vehicleType"
            label="Vehicle Type / Model"
            fullWidth
          />
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
          <TextInput source="vehicleYear" label="Vehicle Year" fullWidth />
          <TextInput source="mortgageFrom" label="Mortgage From" fullWidth />
        </div>

        {/* Expiry Dates */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
          <DateInput
            source="insuranceExpiry"
            label="Insurance Expiry"
            fullWidth
            format={formatDateInput}
            parse={parseDateInput}
          />
          <DateInput
            source="registrationExpiry"
            label="Registration Expiry"
            fullWidth
            format={formatDateInput}
            parse={parseDateInput}
          />
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
          <TextInput
            source="installmentBank"
            label="Installment Deduction Bank Name"
            fullWidth
          />
          <NumberInput
            source="totalFacilityAmount"
            label="Total Facility Amount (AED)"
            fullWidth
          />
        </div>

        <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
          <NumberInput
            source="monthlyInstallment"
            label="Monthly Installment (AED)"
            fullWidth
          />
          <DateInput
            source="installmentStart"
            label="Installment Start Date"
            fullWidth
            format={formatDateInput}
            parse={parseDateInput}
          />
          <DateInput
            source="installmentEnd"
            label="Installment End Date"
            fullWidth
            format={formatDateInput}
            parse={parseDateInput}
          />
        </div>

        {/* Finance Adjustment / Deferment */}
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 8 }}>Finance Adjustment / Deferred Installments</h4>

          <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
            <DateInput
              source="manualRevisedEndDate"
              label="Manual Revised End Date (optional)"
              fullWidth
              format={formatDateInput}
              parse={parseDateInput}
            />
            <NumberInput
              source="bankCharges"
              label="Bank Charges / Interest (AED)"
              fullWidth
            />
          </div>

          <ArrayInput source="deferredInstallments" label="Deferred Month & Year">
            <SimpleFormIterator inline>
              <SelectInput source="month" label="Month" choices={MONTH_CHOICES} />
              <SelectInput source="year" label="Year" choices={yearChoices} />
              <NumberInput source="amount" label="Amount (AED)" />
              <TextInput source="remarks" label="Remarks" />
            </SimpleFormIterator>
          </ArrayInput>

          <div style={{ fontSize: 13, color: "#666" }}>
            Add each deferred installment separately by month and year. If bank does not give revised end date, the system extends the end date automatically by the number of deferred months.
          </div>
        </div>

        {/* Final Settlement */}
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 8 }}>Final Settlement</h4>
          <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
            <DateInput
              source="settlementDate"
              label="Settlement Date"
              fullWidth
              format={formatDateInput}
              parse={parseDateInput}
            />
            <NumberInput
              source="settlementAmount"
              label="Settlement Amount (AED)"
              fullWidth
            />
            <TextInput
              source="settlementNotes"
              label="Settlement Notes / Reference"
              fullWidth
            />
          </div>
          <div style={{ fontSize: 13, color: "#666" }}>
            If Settlement Date is entered and it is on or before the report date, remaining finance becomes 0.
          </div>
        </div>

        {/* Remaining Finance Display */}
        <div
          style={{
            marginTop: 15,
            fontWeight: "bold",
            background: "#f9fafb",
            padding: "10px 14px",
            borderRadius: "6px",
            border: "1px solid #ddd",
            color: "#1b1b1b",
            fontSize: "15px",
          }}
        >
          Remaining Finance Amount: AED{" "}
          <span style={{ color: "#1976d2" }}>
            {remainingDisplay.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>

        {/* Sale / Disposal */}
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 8 }}>Sale / Disposal</h4>
          <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
            <DateInput
              source="soldAt"
              label="Sold Date (optional)"
              fullWidth
              format={formatDateInput}
              parse={parseDateInput}
            />
            <TextInput source="soldNotes" label="Sold Notes (optional)" fullWidth />
          </div>
          <div style={{ fontSize: 13, color: "#666" }}>
            If Sold Date is set, remaining finance will be treated as 0 from that
            date onwards.
          </div>
        </div>

        {/* Upload Documents */}
        <div style={{ marginTop: 25 }}>
          <h4 style={{ marginBottom: 8 }}>Upload Documents</h4>

          <FileInput
            source="insuranceFile"
            label="Insurance Document"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>

          <FileInput
            source="registrationFile"
            label="Registration Document"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>

          <FileInput
            source="financeFile"
            label="Finance Agreement / Mortgage Document"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>
        </div>
      </SimpleForm>
    </Edit>
  );
};
